import store from "../../store";
import * as Flags from "../Flags";
import faker from "../vendor/Faker";
import * as FleetSelectors from "../../store/Fleet/Fleet.selector";
import {
	GLOBAL_FLEET_ID,
	GLOBAL_FLEET_NAME,
} from "../../store/Fleet/Fleet.selector";
import * as strings from "../../strings";

import * as LocaleUtils from "./LocaleUtils";
import * as StringUtils from "./StringUtils";
import * as UserUtils from "./UserUtils";

const NAME_BLACK_LIST = [
	"Prince",
	"Zelda",
	"Wellington",
	"Elmer",
	"Diego",
	"Willy",
	"Lebsack",
	"Diamond",
];

/** @deprecated - use FormatHooks instead */
export const getFleetDisplayName = (fleetId, anonymize = true) => {
	if (fleetId === GLOBAL_FLEET_ID) return GLOBAL_FLEET_NAME;
	if (fleetId === Flags.DEMO_FLEET_ID || fleetId === Flags.DEMO_FLEET_NAME)
		return Flags.DEMO_FLEET_NAME;

	const obfuscateInfo = shouldAnonymizeGroups() && anonymize;
	if (obfuscateInfo) {
		faker.seed(StringUtils.hashCode(fleetId));
		const letter = faker.random.arrayElement([
			"A",
			"B",
			"C",
			"D",
			"E",
			"F",
			"G",
			"H",
			"J",
			"K",
			"L",
			"M",
			"M",
			"N",
			"X",
			"P",
		]);
		return strings.MISC_FLEET.map(
			(fleet) => `${fleet}${fleetId ? " " + letter : ""}`,
		);
	}

	const state = store.getState();
	const fleets = FleetSelectors.getAllFleets(state).value || [];
	const fleet = fleets.find(
		(fleet) => fleetId === fleet.id || fleetId === fleet.name,
	);
	return (fleet && fleet.name) || strings.MISC_FLEET;
};

export const shouldAnonymizeUsers = () => {
	return Flags.ANONYMOUS_MODE.is();
};

export const shouldAnonymizeGroups = () => {
	return Flags.ANONYMOUS_MODE.is() && !Flags.DEMO_MODE.is();
};

/** @deprecated - use FormatHooks instead */
export const getGroupDisplayName = (group, anonymize = true) => {
	const obfuscateInfo = shouldAnonymizeGroups() && anonymize;
	if (!obfuscateInfo) {
		return group?.name || "";
	}
	return strings.MISC_UNIT.map((unit) => `${unit} ${group.unit}`);
};

const findName = () => {
	let fakeFirstName = faker.name.firstName();
	while (NAME_BLACK_LIST.includes(fakeFirstName)) {
		fakeFirstName = faker.name.firstName();
	}
	let fakeLastName = faker.name.lastName();
	while (NAME_BLACK_LIST.includes(fakeLastName)) {
		fakeLastName = faker.name.lastName();
	}
	return [fakeFirstName, fakeLastName];
};

/** @deprecated - use FormatHooks instead **/
export const getUserDisplayName = (
	firstName,
	lastName,
	userId,
	anonymize = true,
) => {
	const obfuscateInfo = shouldAnonymizeUsers() && anonymize;
	if (userId && obfuscateInfo) faker.seed(StringUtils.hashCode(userId));

	if (!firstName || !lastName) {
		if (obfuscateInfo) {
			const [fakeFirstName, fakeLastName] = findName();
			return firstName ? fakeFirstName : fakeLastName;
		}

		const name = firstName || lastName;
		return StringUtils.toTitleCase(name);
	}
	if (obfuscateInfo) {
		const [fakeFirstName, fakeLastName] = findName();
		return `${fakeFirstName} ${fakeLastName}`;
	}

	return `${StringUtils.toTitleCase(firstName)} ${StringUtils.toTitleCase(
		lastName,
	)}`;
};

/** @deprecated - use FormatHooks instead */
export const getDisplayPlateNumber = (
	plateNumber,
	vehicleId,
	anonymize = true,
) => {
	const obfuscateInfo = shouldAnonymizeUsers() && anonymize;

	return obfuscateInfo ? strings.MISC_HIDDEN : plateNumber;
};

/** @deprecated - use FormatHooks instead */
export const getUserDisplayEmail = (email, userId, anonymize = true) => {
	const obfuscateInfo = shouldAnonymizeUsers() && anonymize;
	if (userId && obfuscateInfo) faker.seed(StringUtils.hashCode(userId));

	if (obfuscateInfo) {
		const [fakeFirstName, fakeLastName] = findName();
		return faker.internet.email(fakeFirstName, fakeLastName).toLowerCase();
	}

	return email;
};

/** @deprecated - use FormatHooks instead */
export const getUserDisplayPhoneNumber = (
	phoneNumber,
	userId,
	anonymize = true,
) => {
	const obfuscateInfo = shouldAnonymizeUsers() && anonymize;

	return obfuscateInfo ? strings.MISC_HIDDEN : phoneNumber;
};

export const anonymizeString = (string, locale) => {
	if (typeof string !== "string") {
		return string;
	}
	const state = store.getState();

	const drivers = (state.DriversReducer.drivers || {}).value || [];
	drivers.forEach((driver) => {
		if (driver.uid) {
			const name = getUserDisplayName(
				driver.firstName,
				driver.lastName,
				UserUtils.getDriverKey(driver),
			);
			const nameParts = name.split(" ");
			string = string.replace(driver.firstName, nameParts[0]);
			string = string.replace(driver.lastName, nameParts[1]);
		}
	});

	// Don't change group names in demo mode
	if (shouldAnonymizeGroups()) {
		const groups =
			FleetSelectors.getCurrentScopeFleetGroupsFlat(state).value || [];
		groups.forEach((group) => {
			const name = LocaleUtils.translate(getGroupDisplayName(group), locale);
			string = string.replace(group.id, name);
			string = string.replace(group.name, name);
		});
	}

	const fleets = FleetSelectors.getAllFleets(state).value || [];
	fleets.forEach((fleet) => {
		string = string.replace(
			fleet.name,
			LocaleUtils.translate(getFleetDisplayName(fleet.id), locale),
		);
	});

	return string;
};

const domParser = new DOMParser();

export const formatInsightDisplayCopy = (
	copy,
	locale,
	drivers,
	anonymize = true,
	skipReplace = false,
) => {
	const obfuscateInfo = shouldAnonymizeUsers() && anonymize;
	let copyText = "";

	if (copy) {
		const copyAsHtml = domParser.parseFromString(copyText, "text/html");
		const links = copyAsHtml.getElementsByTagName("a");

		if (links) {
			[...links].forEach((link) => {
				const href = link.getAttribute("href").split("brightmile.io");
				const hrefPath = href[href.length - 1];
				const [_, pathName, driverUid] = hrefPath.split("/");

				if (pathName !== "driver") {
					return;
				}

				const driver = drivers?.find((driver) => driver.uid === driverUid);

				if (!driver) {
					return;
				}

				const [firstName, lastName] = link.textContent.split(" ");
				const anonymisedName = driver.ostrichMode
					? generateInitials({ seed: driverUid, fakeLooking: true })
					: getUserDisplayName(firstName, lastName, driverUid);
				link.textContent = anonymisedName;

				if (driver.ostrichMode && !skipReplace) {
					const linkReplacement = document.createElement("span");
					linkReplacement.innerHTML = link.innerHTML;
					link.parentNode.replaceChild(linkReplacement, link);
				}
			});

			copyText = copyAsHtml.body.innerHTML;
		}

		copyText = obfuscateInfo ? anonymizeString(copyText, locale) : copyText;
	}

	return copyText;
};

export const getHours = (_stat) => {
	return Math.floor(_stat / 60) || 0;
};

export const getMinutes = (_stat) => {
	const _hrs = getHours(_stat);
	const val = _stat - _hrs * 60;
	return val || 0;
};

export const generateName = (options = {}) => {
	return getFaker(options).name.findName();
};

const getFaker = (options = {}) => {
	let { seed } = options;
	if (!seed) {
		seed = Math.random() + Date.now();
	} else if (typeof seed === "string") {
		seed = StringUtils.hashCode(seed);
	}
	faker.seed(seed);
	return faker;
};

export const generateInitials = (options = {}) => {
	const { fakeLooking, seed } = options;
	const name = generateName(options);
	let initials = name
		.split(" ")
		.map((s) => s[0])
		.join("")
		.trim()
		.toUpperCase();
	if (fakeLooking) {
		initials = initials[0] + (Math.abs(StringUtils.hashCode(seed)) % 9);
	}
	return initials;
};

export const getLocaleString = (text, localeCode, fallback = true) => {
	if (!text) {
		return text;
	}

	try {
		const obj = JSON.parse(text);

		if (fallback && (!localeCode || !obj[localeCode])) {
			// Not in current language, fallback to other locale
			if (!obj[LocaleUtils.DEFAULT_LOCALE.code]) {
				// No English, have to fallback to first defined locale
				const keys = Object.keys(obj);

				if (keys.length < 1) {
					// Empty object, return empty string
					return "";
				}

				return obj[keys[0]];
			}

			return obj[LocaleUtils.DEFAULT_LOCALE.code];
		}

		return obj[localeCode];
	} catch {
		return `${text}`;
	}
};
